import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as THREE from "three";
import { fetchWindAlarmWindowsForScenarios } from "../../api/windAlarmWindowsApi";
import { useLazyKeyedResource } from "../../hooks/useLazyKeyedResource";
import { setWindAlarmWindowsIsLoading } from "../../redux/shadingSystem";
import {
  setMaxHoursWindAlarm,
  showWindAlarmWindow,
} from "../../redux/windAlarmWindow";
import { useColorBar } from "../CustomHooks";
import { reduxLocalStorage } from "../utility/storage";
import {
  DEFAULT_WIND_ALARM_METRIC,
  getWindAlarmMetricLabel,
  getWindowMetricValue,
} from "../utility/windAlarmWindows";
import { f, getPolygonCentroid, getRangeModeDisplayColor } from "../utils";
import { parseJsonField } from "../utility/parseJson";
import { VerticalWindowValueLabels } from "./VerticalWindowValueLabels";
import { HEATMAP_LABEL_LAYER } from "./heatmapLabelLayers";

const getWindowPolygon = (windowItem) => {
  const value = parseJsonField(windowItem?.polygon, null);
  if (
    !Array.isArray(value) ||
    typeof value[0] !== "number" ||
    value.length < 9
  ) {
    return null;
  }
  return value;
};

const getWindowLabelPosition = (windowItem) => {
  const polygon = getWindowPolygon(windowItem);
  return polygon ? getPolygonCentroid(polygon) : [0, 0, 0];
};

const WindAlarmWindowMesh = ({
  windowItem,
  metricKey,
  min,
  max,
  rangeMode,
}) => {
  const dispatch = useDispatch();
  const positions = useMemo(() => {
    const polygon = getWindowPolygon(windowItem);
    return polygon ? new Float32Array(polygon) : new Float32Array([]);
  }, [windowItem]);

  const metricValue = getWindowMetricValue(windowItem, metricKey);
  const { visible: inRangeVisible, color } = useMemo(
    () =>
      getRangeModeDisplayColor(metricValue, {
        min,
        max,
        rangeMode,
        colorScale: f,
      }),
    [metricValue, min, max, rangeMode],
  );

  if (!positions.length || !inRangeVisible) return null;

  return (
    <mesh
      dispose={null}
      userData={{ excludeFromLabelOcclusion: true }}
      onClick={(event) => {
        dispatch(showWindAlarmWindow(windowItem));
        event.stopPropagation();
      }}
    >
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={positions}
          count={positions.length / 3}
          itemSize={3}
        />
      </bufferGeometry>
      <meshBasicMaterial
        attach="material"
        side={THREE.DoubleSide}
        color={color}
      />
    </mesh>
  );
};

const WindAlarmWindows = () => {
  const projectId = reduxLocalStorage.getItem("project_id");
  const dispatch = useDispatch();

  const shadingControlScenarios = useSelector(
    (state) => state.sceneVar.shadingControlScenarios,
  );
  const windAlarmByScenario = useSelector(
    (state) => state.shadingSystem.windAlarmSensors,
  );
  const selectedMetric = useSelector(
    (state) =>
      state.windAlarmWindow.selectedMetric || DEFAULT_WIND_ALARM_METRIC,
  );
  const maxHoursWindAlarm = useSelector(
    (state) => state.windAlarmWindow.maxHoursWindAlarm,
  );

  const activeScenarios = useMemo(
    () =>
      (shadingControlScenarios || []).filter(
        (scenario) => windAlarmByScenario[scenario],
      ),
    [shadingControlScenarios, windAlarmByScenario],
  );

  const fetcher = useCallback(
    (scenarios) => fetchWindAlarmWindowsForScenarios(projectId, scenarios),
    [projectId],
  );

  const onLoadingChange = useCallback(
    (loading) => dispatch(setWindAlarmWindowsIsLoading(loading)),
    [dispatch],
  );

  const { dataByKey } = useLazyKeyedResource({
    keys: activeScenarios,
    fetcher,
    enabled: Boolean(projectId) && activeScenarios.length > 0,
    onLoadingChange,
  });

  const allWindows = useMemo(
    () => activeScenarios.flatMap((scenario) => dataByKey[scenario] || []),
    [activeScenarios, dataByKey],
  );

  const labelItems = useMemo(
    () => allWindows.filter((windowItem) => getWindowPolygon(windowItem)),
    [allWindows],
  );

  useEffect(() => {
    if (!allWindows.length) return;
    const maxHours = allWindows.reduce(
      (max, windowItem) =>
        Math.max(max, getWindowMetricValue(windowItem, selectedMetric)),
      0,
    );
    dispatch(setMaxHoursWindAlarm(maxHours));
  }, [allWindows, selectedMetric, dispatch]);

  const dataMax = maxHoursWindAlarm > 0 ? maxHoursWindAlarm : 1;
  const subcategory = getWindAlarmMetricLabel(selectedMetric);
  const { max, min, rangeMode } = useColorBar(
    "Wind Alarm",
    subcategory,
    0,
    dataMax,
  );
  const colorMax = max || dataMax;

  if (!activeScenarios.length || !allWindows.length) return null;

  return (
    <group rotation-x={Math.PI * -0.5} visible>
      {allWindows.map((windowItem) => (
        <WindAlarmWindowMesh
          key={`${windowItem.scenario_name}-${windowItem.name}`}
          windowItem={windowItem}
          metricKey={selectedMetric}
          min={min}
          max={colorMax}
          rangeMode={rangeMode}
        />
      ))}
      <VerticalWindowValueLabels
        visible
        layerKey={HEATMAP_LABEL_LAYER.WIND_ALARM}
        items={labelItems}
        updateKey={`${selectedMetric}-${min}-${colorMax}-${rangeMode}`}
        getVisible={(windowItem) =>
          getRangeModeDisplayColor(
            getWindowMetricValue(windowItem, selectedMetric),
            {
              min,
              max: colorMax,
              rangeMode,
              colorScale: f,
            },
          ).visible
        }
        getPosition={getWindowLabelPosition}
        getValue={(windowItem) =>
          getWindowMetricValue(windowItem, selectedMetric)
        }
        getLabelPolygon={getWindowPolygon}
      />
    </group>
  );
};

export default WindAlarmWindows;
